import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { MapPin, Crosshair, Search, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * MapLocationPicker — Google Maps powered location selector.
 *
 * Props:
 *   value: { lat, lng, location, pincode, city, state }
 *   onChange: ({ lat, lng, location, pincode, city, state }) => void
 *   autoDetect: when true (default) silently auto-fills the location on mount
 *               if the user has previously granted geolocation permission.
 *
 * Features:
 *  - Places Autocomplete search (address / area / city / PIN).
 *  - Draggable marker on the embedded map.
 *  - Reverse geocoding on marker drop → auto-fills location string +
 *    pincode + city + state from Google's address_components.
 *  - "Use My Current Location" button (browser geolocation API).
 *
 * If the API key is missing, renders a polite hint instead of crashing.
 */

const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;

// Extracts a sensible pincode/city/state/location string from a Google
// reverse-geocode result. Mongo-stored format mirrors what
// /api/locations/search returns so existing search/filters keep working.
const parseGeocodeResult = (result) => {
  const comps = result?.address_components || [];
  let pincode = '';
  let city = '';
  let area = '';
  let state = '';
  for (const c of comps) {
    if (c.types.includes('postal_code')) pincode = c.long_name;
    if (c.types.includes('locality')) city = c.long_name;
    if (c.types.includes('sublocality') || c.types.includes('sublocality_level_1')) {
      if (!area) area = c.long_name;
    }
    if (c.types.includes('administrative_area_level_1')) state = c.long_name;
    if (!city && c.types.includes('administrative_area_level_2')) city = c.long_name;
  }
  // Prefer a human-readable formatted address, but fall back to area+city
  let location = result?.formatted_address || '';
  if (!location && (area || city)) location = `${area}${area && city ? ', ' : ''}${city}`;
  return { pincode, city, area, state, location };
};

// Build a clean "Area, City PINCODE" display string from a parsed geocode result.
// Falls back gracefully when fields are missing (rural areas often have no sublocality).
const buildDisplay = (parsed) => {
  const parts = [parsed.area, parsed.city].filter(Boolean);
  const head = parts.length ? parts.join(', ') : (parsed.location || '');
  const display = parsed.pincode ? `${head} ${parsed.pincode}`.trim() : head;
  return display || parsed.location || '';
};

// Pick the best result from a list of geocode candidates (sorted finest→coarsest).
const pickBestResult = (results) => {
  for (const cand of results) {
    const p = parseGeocodeResult(cand);
    if (p.location && (p.area || p.city)) return p;
  }
  return parseGeocodeResult(results[0]);
};

// Internal: places autocomplete bound to the map. Must be a child of <APIProvider>.
// Exposes its input ref upward via the `inputRef` prop so other components inside
// the picker (e.g. LocationControlBar after a successful geolocation) can mirror
// the resolved location string into the visible search box.
const AutocompleteSearchInput = ({ onPick, inputRef, initialValue }) => {
  const placesLib = useMapsLibrary('places');
  const [autocomplete, setAutocomplete] = useState(null);

  // Pre-fill the search box if the listing already has a saved location
  // (EditListing flow). Runs once on mount.
  useEffect(() => {
    if (inputRef?.current && initialValue && !inputRef.current.value) {
      inputRef.current.value = initialValue;
    }
  }, [inputRef, initialValue]);

  useEffect(() => {
    if (!placesLib || !inputRef.current) return;
    const ac = new placesLib.Autocomplete(inputRef.current, {
      fields: ['geometry', 'formatted_address', 'address_components', 'name'],
      componentRestrictions: { country: 'in' },
    });
    setAutocomplete(ac);
  }, [placesLib]);

  useEffect(() => {
    if (!autocomplete) return;
    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place?.geometry?.location) {
        toast.error('Pick a place from the dropdown');
        return;
      }
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const parsed = parseGeocodeResult(place);
      const display = buildDisplay(parsed) || place.formatted_address || place.name || '';
      onPick({ lat, lng, ...parsed, location: display });
    });
    return () => listener.remove?.();
  }, [autocomplete, onPick]);

  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1800ad] pointer-events-none" />
      <input
        ref={inputRef}
        data-testid="map-search-input"
        type="text"
        placeholder="Search address, area, city, or PIN code (India)"
        className="brand-gradient-border-input w-full rounded-2xl pl-10 pr-4 py-3 text-sm text-[#0A0B10] focus:ring-4 focus:ring-[#1800ad]/10 outline-none"
      />
    </div>
  );
};

// Internal: marker + map controller. Recenters the map when value.lat/lng change
// from outside (e.g. autocomplete or geolocation). Reverse-geocodes on drag-end
// using the same library geocoder so we never hit the REST referer-restricted path.
const MarkerAndCenter = ({ value, onChange }) => {
  const map = useMap();
  const geocodingLib = useMapsLibrary('geocoding');
  const geocoder = useMemo(
    () => (geocodingLib ? new geocodingLib.Geocoder() : null),
    [geocodingLib],
  );

  // Recenter when value changes
  useEffect(() => {
    if (!map || !value?.lat || !value?.lng) return;
    map.panTo({ lat: value.lat, lng: value.lng });
    map.setZoom(Math.max(map.getZoom() || 5, 14));
  }, [map, value?.lat, value?.lng]);

  const handleDragEnd = useCallback(async (e) => {
    const lat = e.latLng?.lat();
    const lng = e.latLng?.lng();
    if (lat == null || lng == null) return;
    if (!geocoder) { onChange({ ...value, lat, lng }); return; }
    try {
      const { results } = await geocoder.geocode({ location: { lat, lng }, language: 'en', region: 'IN' });
      if (results && results.length > 0) {
        const parsed = pickBestResult(results);
        onChange({ lat, lng, ...parsed, location: buildDisplay(parsed) });
      } else {
        onChange({ ...value, lat, lng });
      }
    } catch (_err) {
      onChange({ ...value, lat, lng });
    }
  }, [geocoder, onChange, value]);

  if (!value?.lat || !value?.lng) return null;
  return (
    <AdvancedMarker
      position={{ lat: value.lat, lng: value.lng }}
      draggable
      onDragEnd={handleDragEnd}
    />
  );
};

// Internal: the "Use My Current Location" button + status chip + clear-pin.
// Must live INSIDE APIProvider so it can use the geocoding library (which shares
// auth with the embedded Map JS API — REST geocode call is referer-restricted
// on most production deployments and would silently fail).
const LocationControlBar = ({ value, onChange, autoDetect, searchInputRef }) => {
  const geocodingLib = useMapsLibrary('geocoding');
  const geocoder = useMemo(
    () => (geocodingLib ? new geocodingLib.Geocoder() : null),
    [geocodingLib],
  );
  const [geolocating, setGeolocating] = useState(false);
  const autoTriedRef = useRef(false);

  const reverseGeocode = useCallback(async (lat, lng) => {
    if (!geocoder) return [];
    try {
      const { results } = await geocoder.geocode({ location: { lat, lng }, language: 'en', region: 'IN' });
      return results || [];
    } catch (_err) {
      return [];
    }
  }, [geocoder]);

  const handleUseMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by this browser');
      return;
    }
    if (!geocoder) {
      // Maps library still loading — retry shortly so the click never feels dead.
      toast.info('Loading map services… please try again in a moment.');
      return;
    }
    setGeolocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const results = await reverseGeocode(lat, lng);
        if (results.length === 0) {
          // At least move the pin so the user can drag/refine; keep text empty for manual entry.
          onChange({ lat, lng, location: '', pincode: '', city: '', state: '' });
          toast.info('Could not detect your area. Please type your locality or PIN code.');
          setGeolocating(false);
          return;
        }
        const parsed = pickBestResult(results);
        const display = buildDisplay(parsed);
        onChange({ lat, lng, ...parsed, location: display });
        // Mirror the resolved human-readable string into the visible search box
        // so users see exactly what was detected (Amanora, Pune 411013), per spec.
        if (searchInputRef?.current && display) {
          searchInputRef.current.value = display;
        }
        const friendly = parsed.area || parsed.city || parsed.location || 'your location';
        toast.success(`Location set: ${friendly}${parsed.pincode ? ` (${parsed.pincode})` : ''}`);
        setGeolocating(false);
      },
      (err) => {
        setGeolocating(false);
        if (err.code === err.PERMISSION_DENIED) {
          toast.error('Location access denied. Enable in your browser settings.');
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          toast.error('Location unavailable. Check GPS / Wi-Fi and try again.');
        } else if (err.code === err.TIMEOUT) {
          toast.error('Location request timed out. Please retry.');
        } else {
          toast.error('Failed to get your location');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  }, [geocoder, reverseGeocode, onChange]);

  // Silent auto-detect on mount if the user already granted geolocation permission AND
  // the listing has no location yet. Waits for the geocoder so we never re-run into
  // the "could not detect" path with a half-loaded SDK.
  useEffect(() => {
    if (!autoDetect || autoTriedRef.current) return;
    if (value?.lat && value?.lng) return;
    if (!geocoder) return;
    if (!navigator.geolocation || !navigator.permissions?.query) return;
    autoTriedRef.current = true;
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((res) => {
        if (res.state === 'granted') {
          handleUseMyLocation();
        }
      })
      .catch(() => { /* permissions API unavailable — silent no-op */ });
  }, [autoDetect, geocoder, handleUseMyLocation, value?.lat, value?.lng]);

  const clearLocation = () => onChange({ lat: null, lng: null, location: '', pincode: '', city: '', state: '' });
  const hasLocation = value?.lat && value?.lng;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={handleUseMyLocation}
        disabled={geolocating}
        data-testid="map-use-current-location-btn"
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#f0edff] text-[#1800ad] text-xs font-bold hover:bg-[#1800ad] hover:text-white transition-colors disabled:opacity-50"
      >
        <Crosshair size={14} /> {geolocating ? 'Locating…' : 'Use My Current Location'}
      </button>
      {hasLocation && (
        <button
          type="button"
          onClick={clearLocation}
          data-testid="map-clear-location-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-red-50 text-red-600 text-xs font-bold hover:bg-red-600 hover:text-white transition-colors"
        >
          <X size={14} /> Clear pin
        </button>
      )}
      {hasLocation && (
        <span className="text-xs text-[#6B7280] inline-flex items-center gap-1 max-w-full">
          <MapPin size={12} className="text-[#00bf63] flex-shrink-0" />
          <span className="truncate" data-testid="map-current-location-chip">
            {value.location || value.pincode || value.city || `${value.lat.toFixed(4)}, ${value.lng.toFixed(4)}`}
          </span>
        </span>
      )}
    </div>
  );
};

const MapLocationPicker = ({ value, onChange, autoDetect = true }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const searchInputRef = useRef(null);

  const handlePick = useCallback((pick) => {
    onChange(pick);
  }, [onChange]);

  if (!apiKey) {
    return (
      <div className="bg-[#fff4ed] border border-[#ff8500]/30 rounded-2xl p-4 text-sm text-[#0A0B10]" data-testid="map-api-key-missing">
        <p className="font-bold text-[#ff8500] mb-1">Map preview unavailable</p>
        <p className="text-xs text-[#4B5563]">Use the PIN code / city search box below to set your location.</p>
      </div>
    );
  }

  return (
    <APIProvider apiKey={apiKey} libraries={['places', 'geocoding']} language="en" region="IN">
      <div className="space-y-3" data-testid="map-location-picker">
        <AutocompleteSearchInput onPick={handlePick} inputRef={searchInputRef} initialValue={value?.location || ''} />

        <LocationControlBar value={value} onChange={onChange} autoDetect={autoDetect} searchInputRef={searchInputRef} />

        <div className="rounded-2xl overflow-hidden border-2 border-[#1800ad]/20 shadow-sm" style={{ height: 320 }}>
          <Map
            mapId="genzox-postad-map"
            defaultCenter={INDIA_CENTER}
            defaultZoom={DEFAULT_ZOOM}
            gestureHandling="greedy"
            disableDefaultUI={false}
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={false}
            data-testid="map-canvas"
          >
            <MarkerAndCenter value={value} onChange={onChange} />
          </Map>
        </div>

        <p className="text-xs text-[#0A0B10] font-bold" data-testid="map-tip-text">
          Tip: Drag the map pin to fine-tune the exact location. Area, city, and PIN code will be updated automatically.
        </p>
      </div>
    </APIProvider>
  );
};

export default MapLocationPicker;
